<template>
  <!-- Sticky chapters: the steps scroll normally down one side while the chat stays in view on the
       other and switches to the step that sits mid-screen (the AI AutoPilot chat, or with
       `chapter.stage` one of the StoryStage screens) (native position: sticky, no scroll
       capture; Szymon allowed sticky progression on Oct 3 as long as scrolling stays free). On
       phones the chat sits above the steps and plays through the steps once when it comes into
       view. Every step's copy is server-rendered. The stage is the AI AutoPilot chat, or with
       `stage: "booking"` the public booking page (StoryBookingStage). An optional `path` + `linkLabel`
       adds one link under the intro. -->
  <section class="sch">
    <div class="sch-shell">
      <header class="sch-heading">
        <h2>{{ chapter.title }}</h2>
        <p class="sch-intro">{{ chapter.intro }}</p>
        <NuxtLink v-if="chapter.path" :to="localePath(chapter.path)" class="sch-link">
          {{ chapter.linkLabel }} <span aria-hidden="true">-&gt;</span>
        </NuxtLink>
      </header>

      <div class="sch-grid">
        <ol class="sch-steps">
          <li
            v-for="(step, index) in chapter.steps"
            :key="step.title"
            :ref="(el) => (stepEls[index] = el)"
            class="sch-step"
            :class="{ 'is-active': index === active }"
          >
            <span class="sch-num">{{ String(index + 1).padStart(2, "0") }}</span>
            <div>
              <h3>{{ step.title }}</h3>
              <p v-if="step.planKey" class="sch-plan">{{ chapter.planPrefix }} <b translate="no">{{ firstPlan(step.planKey).name }}</b></p>
              <p>{{ step.copy }}</p>
              <ul v-if="step.details?.length">
                <li v-for="detail in step.details" :key="detail">{{ detail }}</li>
              </ul>
            </div>
          </li>
        </ol>
        <div ref="stageEl" class="sch-stage">
          <!-- `stage` picks the app window that follows the steps; the AI chat is the default. -->
          <StoryBookingStage v-if="chapter.stage === 'booking'" :state="state" :sample="chapter.sample" tall />
          <StoryScene v-else-if="chapter.stage === 'inbox' || chapter.stage === 'call'" :autoplay="false" tall>
            <MotionInboxThreadWindow v-if="chapter.stage === 'inbox'" :state="state" />
            <MotionCallWindow v-else :state="state" />
          </StoryScene>
          <StoryReplicaStage v-else-if="chapter.replica" :replica="chapter.replica" :states="states" :state="state" :label="chapter.stageLabel" tall />
          <StoryStage v-else-if="chapter.stage" :screen="chapter.stage" :state="state" :label="chapter.stageLabel" tall />
          <StoryChatStage v-else :state="state" tall />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { firstPlan } from "~/utils/pricingGates";

const props = defineProps({
  chapter: { type: Object, required: true },
});
const { localePath } = useWorkspaceLocale();

const states = computed(() => props.chapter.steps.map((step) => step.state));
const active = ref(props.chapter.steps.length - 1);
const state = computed(() => states.value[active.value] || "done");
const stepEls = [];
const stageEl = ref(null);
let observers = [];
let timers = [];

onMounted(() => {
  const phone = window.matchMedia("(max-width: 1023px)").matches;
  if (phone) {
    // Reduced motion: no autoplay; the chat rests on the result beside the steps.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // Phones: play the steps through once when the chat comes into view, then rest on the last.
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      states.value.forEach((_, index) => {
        timers.push(setTimeout(() => { active.value = index; }, index * 1900));
      });
    }, { threshold: 0.5 });
    active.value = 0;
    observer.observe(stageEl.value);
    observers.push(observer);
    return;
  }
  // Desktop: the step crossing the middle band of the viewport is the active one. This follows
  // the reader's own scrolling, so it runs under reduced motion too (the chat swaps without
  // transitions there).
  active.value = 0;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) active.value = stepEls.indexOf(entry.target);
    });
  }, { rootMargin: "-45% 0px -45% 0px" });
  stepEls.forEach((el) => el && observer.observe(el));
  observers.push(observer);
});

onBeforeUnmount(() => {
  observers.forEach((observer) => observer.disconnect());
  timers.forEach(clearTimeout);
});
</script>

<style scoped>
.sch { background: #fff; padding: 6.5rem 0; }
.dark .sch { background: #020617; }
.sch-shell { width: min(100% - 3rem, var(--story-max, 84rem)); margin: 0 auto; }
.sch-heading { max-width: 48rem; }
.sch-heading h2 {
  color: #020617;
  font-size: clamp(2rem, 4vw, 3.25rem);
  font-weight: 880;
  line-height: 1.04;
  text-wrap: balance;
}
.dark .sch-heading h2 { color: #f8fafc; }
.sch-intro { margin-top: 1rem; max-width: 41rem; color: #475569; font-size: 1rem; line-height: 1.7; }
.dark .sch-intro { color: #cbd5e1; }
.sch-link { display: inline-flex; gap: 0.4rem; margin-top: 1rem; color: #0e7490; font-weight: 800; }
.dark .sch-link { color: #67e8f9; }
.sch-link:hover { text-decoration: underline; }

.sch-grid { display: grid; gap: 2.5rem; margin-top: 3rem; }
.sch-steps { display: grid; gap: 1rem; margin: 0; padding: 0; list-style: none; }
.sch-step {
  display: grid;
  grid-template-columns: 2.5rem 1fr;
  gap: 0.75rem;
  border-left: 2px solid #e2e8f0;
  padding: 1.25rem 0 1.25rem 1.25rem;
  transition: border-color 0.3s ease, opacity 0.3s ease;
}
.dark .sch-step { border-left-color: #1e293b; }
.sch-num { color: #64748b; font-size: 0.8rem; font-weight: 800; font-variant-numeric: tabular-nums; padding-top: 0.3rem; }
.dark .sch-num { color: #94a3b8; }
.sch-step h3 { color: #0f172a; font-size: 1.3rem; font-weight: 800; line-height: 1.25; }
.dark .sch-step h3 { color: #f1f5f9; }
.sch-step p { margin-top: 0.5rem; color: #475569; font-size: 1rem; line-height: 1.7; }
.dark .sch-step p { color: #cbd5e1; }
/* A step's plan gate (`planKey`, read from data/pricing-features.json). */
.sch-step p.sch-plan { display: inline-block; margin-top: 0.4rem; border: 1px solid #cbd5e1; border-radius: 5px; padding: 0.1rem 0.45rem; color: #334155; font-size: 0.78rem; line-height: 1.5; }
.dark .sch-step p.sch-plan { border-color: #334155; color: #cbd5e1; }
.sch-step ul { display: grid; gap: 0.35rem; margin-top: 0.75rem; padding: 0; list-style: none; }
.sch-step ul li { position: relative; padding-left: 1rem; color: #334155; font-size: 0.92rem; line-height: 1.5; }
.dark .sch-step ul li { color: #cbd5e1; }
.sch-step ul li::before { content: ""; position: absolute; top: 0.6em; left: 0; width: 6px; height: 6px; border-radius: 2px; background: #1ba7ce; }

@media (min-width: 1024px) {
  .sch-grid { grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr); gap: 4rem; align-items: start; }
  .sch-steps { gap: 0; padding: 12vh 0 18vh; }
  .sch-step { min-height: 46vh; align-content: center; }
  /* Inactive steps stay readable (WCAG AA); the rail, number and title colour mark the active one. */
  .sch-step:not(.is-active) h3 { color: #475569; }
  .dark .sch-step:not(.is-active) h3 { color: #b6c2d1; }
  .sch-step.is-active { border-left-color: #1ba7ce; }
  .sch-step.is-active .sch-num { color: #0e7490; }
  .dark .sch-step.is-active .sch-num { color: #67e8f9; }
  /* The chat stays centred in view while the steps scroll past it. */
  .sch-stage { position: sticky; top: max(96px, calc(50vh - 260px)); order: 2; }
}
/* Short desktop windows can't hold the chat below the fixed header, so it scrolls with the page. */
@media (min-width: 1024px) and (max-height: 699px) {
  .sch-stage { position: static; }
}
@media (max-width: 1023px) {
  .sch { padding: 4.5rem 0; }
  .sch-stage { order: -1; }
}
@media (max-width: 639px) {
  .sch-shell { width: min(100% - 2rem, var(--story-max, 84rem)); }
  .sch-step { grid-template-columns: 1fr; gap: 0.35rem; padding-left: 1rem; }
}
</style>
